// frontend/src/pages/Contact.jsx
import React, { useState, useEffect } from "react";
import { motion } from "framer-motion";
import { Link } from "react-router-dom";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faMapMarkerAlt,
  faPhone,
  faEnvelope,
  faClock,
  faPaperPlane,
  faCheckCircle,
  faSpinner,
  faUser,
  faBuilding,
  faComment,
  faArrowRight,
  faExclamationTriangle,
} from "@fortawesome/free-solid-svg-icons";
import { faWhatsapp as faWhatsappBrand } from "@fortawesome/free-brands-svg-icons";
import Button from "../components/UI/Button";
import { createContactMessage } from "../api";
import { useAuth } from "../context/AuthContext";

const Contact = () => {
  const { user } = useAuth();
  const [formData, setFormData] = useState({
    name: "",
    email: "",
    phone: "",
    subject: "",
    message: "",
    category: "general",
  });
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [error, setError] = useState("");
  const [successData, setSuccessData] = useState(null);
  const [activeFaq, setActiveFaq] = useState(null);

  // Auto-fill form if user is logged in
  useEffect(() => {
    if (user) {
      setFormData((prev) => ({
        ...prev,
        name: user.name || "",
        email: user.email || "",
        phone: user.phone || "",
      }));
    }
  }, [user]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
    setError("");
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    setError("");

    // Validate
    if (!formData.name.trim()) {
      setError("Please enter your full name");
      setSubmitting(false);
      return;
    }
    if (!formData.email.trim() || !formData.email.includes("@")) {
      setError("Please enter a valid email address");
      setSubmitting(false);
      return;
    }
    if (!formData.message.trim() || formData.message.length < 10) {
      setError("Please enter a message (minimum 10 characters)");
      setSubmitting(false);
      return;
    }

    try {
      const response = await createContactMessage({
        name: formData.name,
        email: formData.email,
        phone: formData.phone || "",
        subject: formData.subject || "General Inquiry",
        message: formData.message,
        category: formData.category,
        source: "website",
      });

      setSuccessData(response.data);
      setSubmitted(true);
      setFormData((prev) => ({
        ...prev,
        name: user?.name || "",
        email: user?.email || "",
        phone: user?.phone || "",
        subject: "",
        message: "",
        category: "general",
      }));
    } catch (err) {
      console.error("Contact form error:", err);
      const errorMessage =
        err.response?.data?.message ||
        err.response?.data?.error ||
        "Something went wrong. Please try again.";
      setError(errorMessage);
    } finally {
      setSubmitting(false);
    }
  };

  const toggleFaq = (index) => {
    setActiveFaq(activeFaq === index ? null : index);
  };

  const contactInfo = [
    {
      icon: faMapMarkerAlt,
      title: "Visit Us",
      details: ["30 Kissy Road, Freetown, Sierra Leone"],
      color: "#006b2e",
    },
    {
      icon: faPhone,
      title: "Call Us",
      details: ["+232 31 030808", "+1 949 267 8678"],
      color: "#d9a321",
    },
    {
      icon: faEnvelope,
      title: "Email Us",
      details: ["kenalienterprisesl@gmail.com"],
      color: "#3b82f6",
    },
    {
      icon: faWhatsappBrand,
      title: "WhatsApp",
      details: ["+232 31 030808"],
      color: "#25d366",
    },
    {
      icon: faClock,
      title: "Business Hours",
      details: [
        "Monday - Friday: 8:00 AM - 6:00 PM",
        "Saturday: 9:00 AM - 2:00 PM",
        "Sunday: Closed",
      ],
      color: "#6b7280",
    },
  ];

  const categories = [
    { value: "general", label: "General Inquiry" },
    { value: "sales", label: "Sales Inquiry" },
    { value: "support", label: "Customer Support" },
    { value: "partnership", label: "Partnership" },
    { value: "supplier", label: "Supplier Registration" },
    { value: "farmer", label: "Farmer Support" },
  ];

  const faqs = [
    {
      q: "What products does Kenali Enterprise offer?",
      a: "We offer premium livestock nutrition products including Rumen Bypass Fat (GrowPro™), DairyBoost™ dairy supplement, Mineralite™ mineral salt blocks, and protein concentrates. All products are sourced from trusted manufacturers in India.",
    },
    {
      q: "Do you ship across Sierra Leone?",
      a: "Yes, we deliver to farmers across all districts in Sierra Leone, including Freetown, Makeni, Bo, Kenema, Koidu, and other major towns. We offer both bulk and small-package delivery options.",
    },
    {
      q: "What are your payment options?",
      a: "We accept Cash on Delivery, Bank Transfers, Mobile Money (Orange Money, Afrimoney, WAVE), and Credit terms for approved farmers. We also offer deposit options for bulk orders.",
    },
    {
      q: "How can I become a distributor or reseller?",
      a: "We're always looking for reliable distribution partners. Contact us to discuss distributor opportunities, wholesale pricing, and partnership terms. We offer competitive margins for resellers.",
    },
    {
      q: "What is the minimum order quantity?",
      a: "For retail customers, the minimum order is 2kg. For farmers and bulk buyers, we offer 25kg, 50kg, and 100kg+ options. Bulk orders receive significant discounts.",
    },
    {
      q: "How do I track my order?",
      a: "You can track your order using the tracking number provided in your confirmation email. Visit our order tracking page on the website or contact our support team for assistance.",
    },
  ];

  return (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      transition={{ duration: 0.5 }}
      className="contact-page"
    >
      {/* Hero Section */}
      <section className="page-hero">
        <div className="page-hero-overlay" />
        <div className="container page-hero-content">
          <motion.div
            initial={{ y: 30, opacity: 0 }}
            animate={{ y: 0, opacity: 1 }}
            transition={{ delay: 0.2 }}
          >
            <FontAwesomeIcon icon={faEnvelope} className="page-hero-icon" />
            <h1 className="page-hero-title">Contact Us</h1>
            <p className="page-hero-subtitle">
              Have questions about our products or want to place an order? Reach
              out to us today. We're here to help you find the right nutrition
              solutions for your livestock.
            </p>
          </motion.div>
        </div>
      </section>

      {/* Contact Section */}
      <section className="contact-section">
        <div className="container">
          <div className="contact-grid">
            {/* Contact Form */}
            <motion.div
              initial={{ x: -30, opacity: 0 }}
              whileInView={{ x: 0, opacity: 1 }}
              viewport={{ once: true }}
              transition={{ duration: 0.5 }}
              className="contact-form-wrapper"
            >
              <div className="form-header">
                <h2 className="contact-form-title">Send Us a Message</h2>
                <p className="contact-form-subtitle">
                  We'll get back to you within 24 hours.
                  {user && (
                    <span className="user-badge">
                      <FontAwesomeIcon icon={faCheckCircle} />
                      Logged in as {user.name}
                    </span>
                  )}
                </p>
              </div>

              {submitted ? (
                <motion.div
                  initial={{ scale: 0.9, opacity: 0 }}
                  animate={{ scale: 1, opacity: 1 }}
                  className="success-message"
                >
                  <FontAwesomeIcon
                    icon={faCheckCircle}
                    className="success-icon"
                  />
                  <h3>Message Sent Successfully!</h3>
                  <p>
                    Thank you for reaching out,{" "}
                    <strong>{formData.name || "valued customer"}</strong>. We'll
                    respond shortly.
                  </p>
                  {successData?.data?._id && (
                    <p className="reference-number">
                      Reference: #{successData.data._id.slice(-8)}
                    </p>
                  )}
                  <div className="success-actions">
                    <Button
                      variant="outline"
                      size="small"
                      onClick={() => {
                        setSubmitted(false);
                        setSuccessData(null);
                      }}
                      className="success-btn"
                    >
                      Send Another Message
                    </Button>
                    <Button href="/products" variant="primary" size="small">
                      Browse Products
                    </Button>
                  </div>
                </motion.div>
              ) : (
                <form onSubmit={handleSubmit} className="contact-form">
                  <div className="form-row">
                    <div className="form-group">
                      <label>
                        <FontAwesomeIcon icon={faUser} />
                        Full Name *
                      </label>
                      <input
                        type="text"
                        name="name"
                        value={formData.name}
                        onChange={handleChange}
                        required
                        placeholder="Enter your full name"
                        className={error && !formData.name ? "error" : ""}
                      />
                    </div>

                    <div className="form-group">
                      <label>
                        <FontAwesomeIcon icon={faEnvelope} />
                        Email Address *
                      </label>
                      <input
                        type="email"
                        name="email"
                        value={formData.email}
                        onChange={handleChange}
                        required
                        placeholder="you@example.com"
                        className={error && !formData.email ? "error" : ""}
                      />
                    </div>
                  </div>

                  <div className="form-row">
                    <div className="form-group">
                      <label>
                        <FontAwesomeIcon icon={faPhone} />
                        Phone Number
                      </label>
                      <input
                        type="tel"
                        name="phone"
                        value={formData.phone}
                        onChange={handleChange}
                        placeholder="+232 76 123456"
                      />
                    </div>

                    <div className="form-group">
                      <label>
                        <FontAwesomeIcon icon={faBuilding} />
                        Category
                      </label>
                      <select
                        name="category"
                        value={formData.category}
                        onChange={handleChange}
                      >
                        {categories.map((cat) => (
                          <option key={cat.value} value={cat.value}>
                            {cat.label}
                          </option>
                        ))}
                      </select>
                    </div>
                  </div>

                  <div className="form-group">
                    <label>
                      <FontAwesomeIcon icon={faComment} />
                      Subject
                    </label>
                    <input
                      type="text"
                      name="subject"
                      value={formData.subject}
                      onChange={handleChange}
                      placeholder="What's this about?"
                    />
                  </div>

                  <div className="form-group">
                    <label>
                      <FontAwesomeIcon icon={faComment} />
                      Message *
                    </label>
                    <textarea
                      name="message"
                      value={formData.message}
                      onChange={handleChange}
                      required
                      rows="5"
                      placeholder="Tell us how we can help you..."
                      className={error && !formData.message ? "error" : ""}
                    />
                    <div className="char-counter">
                      {formData.message.length} / 5000 characters
                    </div>
                  </div>

                  {error && (
                    <div className="error-message">
                      <FontAwesomeIcon icon={faExclamationTriangle} />
                      {error}
                    </div>
                  )}

                  <Button
                    type="submit"
                    variant="primary"
                    size="large"
                    disabled={submitting}
                    fullWidth
                  >
                    {submitting ? (
                      <>
                        <FontAwesomeIcon icon={faSpinner} spin />
                        Sending...
                      </>
                    ) : (
                      <>
                        <FontAwesomeIcon icon={faPaperPlane} />
                        Send Message
                      </>
                    )}
                  </Button>

                  <p className="form-footer">
                    <FontAwesomeIcon icon={faCheckCircle} />
                    We respect your privacy. Your information is secure.
                  </p>
                </form>
              )}
            </motion.div>

            {/* Contact Info */}
            <motion.div
              initial={{ x: 30, opacity: 0 }}
              whileInView={{ x: 0, opacity: 1 }}
              viewport={{ once: true }}
              transition={{ duration: 0.5 }}
              className="contact-info-wrapper"
            >
              <h2 className="contact-info-title">Get in Touch</h2>
              <p className="contact-info-subtitle">
                We're here to answer your questions and help you find the right
                nutrition solutions for your livestock.
              </p>

              <div className="contact-info-list">
                {contactInfo.map((item, index) => (
                  <motion.div
                    key={index}
                    initial={{ y: 20, opacity: 0 }}
                    whileInView={{ y: 0, opacity: 1 }}
                    transition={{ delay: index * 0.1 }}
                    viewport={{ once: true }}
                    className="contact-info-item"
                  >
                    <div
                      className="contact-info-icon"
                      style={{
                        background: `${item.color}15`,
                        color: item.color,
                      }}
                    >
                      <FontAwesomeIcon icon={item.icon} />
                    </div>
                    <div className="contact-info-details">
                      <h4>{item.title}</h4>
                      {item.details.map((detail, i) => (
                        <p key={i}>{detail}</p>
                      ))}
                    </div>
                  </motion.div>
                ))}
              </div>

              {/* Quick Contact Buttons */}
              <div className="quick-contact">
                <a
                  href="https://wa.me/23231030808"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="quick-contact-btn whatsapp"
                >
                  <FontAwesomeIcon icon={faWhatsappBrand} />
                  WhatsApp Us
                </a>
                <a href="tel:+23231030808" className="quick-contact-btn call">
                  <FontAwesomeIcon icon={faPhone} />
                  Call Us
                </a>
                <a
                  href="mailto:kenalienterprisesl@gmail.com"
                  className="quick-contact-btn email"
                >
                  <FontAwesomeIcon icon={faEnvelope} />
                  Email Us
                </a>
              </div>

              {/* Map Placeholder */}
              <div className="map-placeholder">
                <FontAwesomeIcon icon={faMapMarkerAlt} className="map-icon" />
                <span>30 Kissy Road, Freetown, Sierra Leone</span>
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* FAQ Section */}
      <section className="faq-section">
        <div className="container">
          <div className="section-header">
            <span className="section-tag">FAQ</span>
            <h2>Frequently Asked Questions</h2>
            <p className="section-subtitle">
              Quick answers to the most common questions from our customers
            </p>
          </div>
          <div className="faq-grid">
            {faqs.map((faq, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ delay: index * 0.1 }}
                viewport={{ once: true }}
                className={`faq-card ${activeFaq === index ? "active" : ""}`}
                onClick={() => toggleFaq(index)}
              >
                <div className="faq-header">
                  <h4>{faq.q}</h4>
                  <span className="faq-toggle">
                    {activeFaq === index ? "−" : "+"}
                  </span>
                </div>
                {activeFaq === index && (
                  <motion.div
                    initial={{ opacity: 0, height: 0 }}
                    animate={{ opacity: 1, height: "auto" }}
                    transition={{ duration: 0.3 }}
                    className="faq-answer"
                  >
                    <p>{faq.a}</p>
                  </motion.div>
                )}
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="cta-section">
        <div className="container">
          <div className="cta-content">
            <h2>Ready to Work with Us?</h2>
            <p>Let's find the right nutrition solution for your livestock.</p>
            <div className="cta-buttons">
              <Button href="/products" variant="primary" size="large">
                Explore Products <FontAwesomeIcon icon={faArrowRight} />
              </Button>
              <Button href="/register" variant="outline" size="large">
                Register as Farmer
              </Button>
            </div>
          </div>
        </div>
      </section>

      <style>{`
        /* ============================================
           CONTACT PAGE STYLES - Mobile-First
           ============================================ */

        .contact-page {
          min-height: 100vh;
          background: #ffffff;
        }

        /* Hero Section */
        .page-hero {
          background: linear-gradient(135deg, #006b2e 0%, #014421 100%);
          color: #ffffff;
          padding: 3rem 1.5rem;
          text-align: center;
          position: relative;
          overflow: hidden;
        }
        @media (min-width: 768px) {
          .page-hero {
            padding: 4rem 2rem;
          }
        }

        .page-hero-overlay {
          position: absolute;
          top: -50%;
          right: -20%;
          width: 300px;
          height: 300px;
          background: rgba(217, 163, 33, 0.05);
          border-radius: 50%;
        }
        @media (min-width: 768px) {
          .page-hero-overlay {
            width: 500px;
            height: 500px;
          }
        }

        .page-hero-content {
          position: relative;
          z-index: 2;
        }

        .page-hero-icon {
          font-size: 2rem;
          color: #d9a321;
          margin-bottom: 0.75rem;
        }
        @media (min-width: 768px) {
          .page-hero-icon {
            font-size: 2.5rem;
            margin-bottom: 1rem;
          }
        }

        .page-hero-title {
          font-size: clamp(1.75rem, 5vw, 2.5rem);
          margin-bottom: 0.5rem;
          font-family: "Montserrat", sans-serif;
        }

        .page-hero-subtitle {
          font-size: 0.938rem;
          max-width: 600px;
          margin: 0 auto;
          opacity: 0.9;
          line-height: 1.7;
        }
        @media (min-width: 768px) {
          .page-hero-subtitle {
            font-size: 1.125rem;
          }
        }

        /* Container */
        .container {
          max-width: 1280px;
          margin: 0 auto;
          padding: 0 1rem;
        }
        @media (min-width: 768px) {
          .container {
            padding: 0 2rem;
          }
        }

        /* Contact Section */
        .contact-section {
          padding: 2rem 0;
        }
        @media (min-width: 768px) {
          .contact-section {
            padding: 4rem 0;
          }
        }

        .contact-grid {
          display: grid;
          grid-template-columns: 1fr;
          gap: 2rem;
        }
        @media (min-width: 768px) {
          .contact-grid {
            grid-template-columns: 1fr 1fr;
            gap: 3rem;
          }
        }
        @media (min-width: 1024px) {
          .contact-grid {
            gap: 4rem;
          }
        }

        /* Contact Form */
        .contact-form-wrapper {
          order: 2;
        }
        @media (min-width: 768px) {
          .contact-form-wrapper {
            order: 1;
          }
        }

        .form-header {
          margin-bottom: 1.5rem;
        }

        .contact-form-title {
          color: #006b2e;
          font-size: 1.25rem;
          margin-bottom: 0.5rem;
          font-family: "Montserrat", sans-serif;
        }
        @media (min-width: 768px) {
          .contact-form-title {
            font-size: 1.5rem;
          }
        }

        .contact-form-subtitle {
          color: #5a5a5a;
          font-size: 0.875rem;
          display: flex;
          flex-wrap: wrap;
          align-items: center;
          gap: 0.5rem;
        }
        @media (min-width: 768px) {
          .contact-form-subtitle {
            font-size: 0.938rem;
          }
        }

        .user-badge {
          display: inline-flex;
          align-items: center;
          gap: 0.25rem;
          font-size: 0.75rem;
          color: #16a34a;
          background: #dcfce7;
          padding: 0.15rem 0.5rem;
          border-radius: 12px;
          font-weight: 500;
        }

        .contact-form .form-group {
          margin-bottom: 1rem;
        }
        @media (min-width: 768px) {
          .contact-form .form-group {
            margin-bottom: 1.25rem;
          }
        }

        .contact-form .form-group label {
          display: block;
          margin-bottom: 0.375rem;
          font-weight: 500;
          font-size: 0.813rem;
          color: #1f1f1f;
        }
        @media (min-width: 768px) {
          .contact-form .form-group label {
            font-size: 0.875rem;
            margin-bottom: 0.5rem;
          }
        }

        .contact-form .form-group label svg {
          margin-right: 0.5rem;
          color: #d9a321;
          font-size: 0.813rem;
        }

        .contact-form .form-group input,
        .contact-form .form-group select,
        .contact-form .form-group textarea {
          width: 100%;
          padding: 0.625rem 0.75rem;
          border: 1px solid #e5e5e5;
          border-radius: 8px;
          font-size: 0.875rem;
          transition: border-color 0.3s ease;
          font-family: "Inter", sans-serif;
          background: #ffffff;
          color: #1f1f1f;
        }
        @media (min-width: 768px) {
          .contact-form .form-group input,
          .contact-form .form-group select,
          .contact-form .form-group textarea {
            padding: 0.75rem;
            font-size: 1rem;
          }
        }

        .contact-form .form-group input:focus,
        .contact-form .form-group select:focus,
        .contact-form .form-group textarea:focus {
          outline: none;
          border-color: #d9a321;
          box-shadow: 0 0 0 3px rgba(217, 163, 33, 0.15);
        }

        .contact-form .form-group input.error,
        .contact-form .form-group textarea.error {
          border-color: #dc2626;
        }

        .contact-form .form-group input::placeholder,
        .contact-form .form-group textarea::placeholder {
          color: #5a5a5a;
          opacity: 0.6;
        }

        .contact-form .form-group textarea {
          resize: vertical;
          min-height: 100px;
        }
        @media (min-width: 768px) {
          .contact-form .form-group textarea {
            min-height: 120px;
          }
        }

        .char-counter {
          font-size: 0.65rem;
          color: #6b7280;
          text-align: right;
          margin-top: 0.25rem;
        }

        .form-row {
          display: grid;
          grid-template-columns: 1fr 1fr;
          gap: 0.75rem;
        }
        @media (max-width: 576px) {
          .form-row {
            grid-template-columns: 1fr;
          }
        }

        .error-message {
          background: #fee2e2;
          color: #dc2626;
          padding: 0.625rem 0.875rem;
          border-radius: 8px;
          margin-bottom: 1rem;
          font-size: 0.813rem;
          display: flex;
          align-items: center;
          gap: 0.5rem;
        }

        .form-footer {
          font-size: 0.7rem;
          color: #6b7280;
          text-align: center;
          margin-top: 0.75rem;
          display: flex;
          align-items: center;
          justify-content: center;
          gap: 0.5rem;
        }

        .success-message {
          background: #dcfce7;
          color: #16a34a;
          padding: 1.5rem;
          border-radius: 12px;
          text-align: center;
        }
        @media (min-width: 768px) {
          .success-message {
            padding: 2rem;
          }
        }

        .success-icon {
          font-size: 2.5rem;
          margin-bottom: 0.75rem;
        }
        @media (min-width: 768px) {
          .success-icon {
            font-size: 3rem;
            margin-bottom: 1rem;
          }
        }

        .success-message h3 {
          color: #16a34a;
          font-size: 1.125rem;
          margin-bottom: 0.5rem;
        }
        @media (min-width: 768px) {
          .success-message h3 {
            font-size: 1.25rem;
          }
        }

        .success-message p {
          color: #16a34a;
          opacity: 0.8;
          font-size: 0.875rem;
          margin-bottom: 0.5rem;
        }

        .reference-number {
          font-size: 0.75rem;
          color: #065f46;
          opacity: 0.7;
          margin-bottom: 1rem;
          font-weight: 600;
        }

        .success-actions {
          display: flex;
          gap: 0.75rem;
          justify-content: center;
          flex-wrap: wrap;
          margin-top: 0.5rem;
        }

        .success-btn {
          margin-top: 0.5rem;
        }

        /* Contact Info */
        .contact-info-wrapper {
          order: 1;
        }
        @media (min-width: 768px) {
          .contact-info-wrapper {
            order: 2;
          }
        }

        .contact-info-title {
          color: #006b2e;
          font-size: 1.25rem;
          margin-bottom: 0.5rem;
          font-family: "Montserrat", sans-serif;
        }
        @media (min-width: 768px) {
          .contact-info-title {
            font-size: 1.5rem;
            margin-bottom: 1rem;
          }
        }

        .contact-info-subtitle {
          color: #5a5a5a;
          margin-bottom: 1.5rem;
          font-size: 0.875rem;
        }
        @media (min-width: 768px) {
          .contact-info-subtitle {
            margin-bottom: 2rem;
            font-size: 0.938rem;
          }
        }

        .contact-info-list {
          display: flex;
          flex-direction: column;
          gap: 0.75rem;
        }
        @media (min-width: 768px) {
          .contact-info-list {
            gap: 1rem;
          }
        }

        .contact-info-item {
          display: flex;
          gap: 0.75rem;
          padding: 0.75rem 1rem;
          background: #f8f8f8;
          border-radius: 12px;
          align-items: flex-start;
          transition: all 0.3s ease;
        }
        @media (min-width: 768px) {
          .contact-info-item {
            gap: 1rem;
            padding: 1rem 1.25rem;
          }
        }

        .contact-info-item:hover {
          transform: translateX(4px);
          background: #f0f0f0;
        }

        .contact-info-icon {
          width: 36px;
          height: 36px;
          min-width: 36px;
          border-radius: 50%;
          display: flex;
          align-items: center;
          justify-content: center;
          flex-shrink: 0;
        }
        @media (min-width: 768px) {
          .contact-info-icon {
            width: 44px;
            height: 44px;
            min-width: 44px;
          }
        }

        .contact-info-icon svg {
          font-size: 0.875rem;
        }
        @media (min-width: 768px) {
          .contact-info-icon svg {
            font-size: 1.1rem;
          }
        }

        .contact-info-details h4 {
          font-size: 0.813rem;
          color: #006b2e;
          margin-bottom: 0.125rem;
          font-family: "Montserrat", sans-serif;
        }
        @media (min-width: 768px) {
          .contact-info-details h4 {
            font-size: 0.875rem;
            margin-bottom: 0.25rem;
          }
        }

        .contact-info-details p {
          font-size: 0.813rem;
          color: #5a5a5a;
          line-height: 1.5;
          margin: 0;
        }
        @media (min-width: 768px) {
          .contact-info-details p {
            font-size: 0.875rem;
            line-height: 1.6;
          }
        }

        .quick-contact {
          display: grid;
          grid-template-columns: 1fr 1fr 1fr;
          gap: 0.5rem;
          margin: 1rem 0;
        }
        @media (max-width: 576px) {
          .quick-contact {
            grid-template-columns: 1fr;
          }
        }

        .quick-contact-btn {
          display: flex;
          align-items: center;
          justify-content: center;
          gap: 0.5rem;
          padding: 0.5rem 0.75rem;
          border-radius: 8px;
          text-decoration: none;
          font-size: 0.75rem;
          font-weight: 500;
          transition: all 0.3s ease;
          color: #ffffff;
        }

        .quick-contact-btn.whatsapp {
          background: #25d366;
        }

        .quick-contact-btn.call {
          background: #3b82f6;
        }

        .quick-contact-btn.email {
          background: #ef4444;
        }

        .quick-contact-btn:hover {
          transform: translateY(-2px);
          box-shadow: 0 4px 12px rgba(0, 0, 0, 0.15);
        }

        .map-placeholder {
          margin-top: 1rem;
          background: #e8e5df;
          border-radius: 12px;
          height: 150px;
          display: flex;
          align-items: center;
          justify-content: center;
          color: #5a5a5a;
          gap: 0.5rem;
        }
        @media (min-width: 768px) {
          .map-placeholder {
            margin-top: 1.5rem;
            height: 200px;
          }
        }

        .map-icon {
          font-size: 1.5rem;
          color: #d9a321;
        }
        @media (min-width: 768px) {
          .map-icon {
            font-size: 2rem;
          }
        }

        /* FAQ Section */
        .faq-section {
          background: #f8f8f8;
          padding: 2rem 0;
        }
        @media (min-width: 768px) {
          .faq-section {
            padding: 4rem 0;
          }
        }

        .section-header {
          text-align: center;
          margin-bottom: 2rem;
        }
        @media (min-width: 768px) {
          .section-header {
            margin-bottom: 3rem;
          }
        }

        .section-tag {
          display: inline-block;
          background: rgba(217, 163, 33, 0.1);
          color: #d9a321;
          padding: 0.25rem 0.75rem;
          border-radius: 20px;
          font-size: 0.75rem;
          font-weight: 600;
          margin-bottom: 0.5rem;
          letter-spacing: 0.5px;
        }
        @media (min-width: 768px) {
          .section-tag {
            margin-bottom: 1rem;
          }
        }

        .section-header h2 {
          color: #006b2e;
          font-size: 1.5rem;
          margin-bottom: 0.5rem;
          font-family: "Montserrat", sans-serif;
        }
        @media (min-width: 768px) {
          .section-header h2 {
            font-size: 2rem;
          }
        }

        .section-subtitle {
          color: #5a5a5a;
          max-width: 600px;
          margin: 0 auto;
          font-size: 0.875rem;
        }
        @media (min-width: 768px) {
          .section-subtitle {
            font-size: 0.938rem;
          }
        }

        .faq-grid {
          display: grid;
          grid-template-columns: 1fr;
          gap: 0.75rem;
          max-width: 800px;
          margin: 0 auto;
        }
        @media (min-width: 768px) {
          .faq-grid {
            gap: 1rem;
          }
        }

        .faq-card {
          background: #ffffff;
          padding: 1rem 1.25rem;
          border-radius: 12px;
          box-shadow: 0 2px 8px rgba(0, 0, 0, 0.05);
          transition: all 0.3s ease;
          cursor: pointer;
        }
        @media (min-width: 768px) {
          .faq-card {
            padding: 1.25rem 1.5rem;
          }
        }

        .faq-card:hover {
          box-shadow: 0 4px 16px rgba(0, 0, 0, 0.08);
        }

        .faq-card.active {
          border-left: 3px solid #d9a321;
          background: #fafafa;
        }

        .faq-header {
          display: flex;
          justify-content: space-between;
          align-items: center;
          gap: 1rem;
        }

        .faq-header h4 {
          color: #006b2e;
          font-size: 0.938rem;
          font-family: "Montserrat", sans-serif;
          margin: 0;
          flex: 1;
        }
        @media (min-width: 768px) {
          .faq-header h4 {
            font-size: 1rem;
          }
        }

        .faq-toggle {
          font-size: 1.25rem;
          color: #d9a321;
          font-weight: 700;
          flex-shrink: 0;
        }

        .faq-answer {
          margin-top: 0.75rem;
          padding-top: 0.75rem;
          border-top: 1px solid #e5e5e5;
        }

        .faq-answer p {
          color: #5a5a5a;
          font-size: 0.875rem;
          line-height: 1.7;
          margin: 0;
        }

        /* CTA Section */
        .cta-section {
          background: linear-gradient(135deg, #006b2e 0%, #014421 100%);
          padding: 2rem 1.5rem;
          text-align: center;
          color: #ffffff;
        }
        @media (min-width: 768px) {
          .cta-section {
            padding: 3rem 2rem;
          }
        }

        .cta-content {
          max-width: 600px;
          margin: 0 auto;
        }

        .cta-content h2 {
          font-size: 1.25rem;
          margin-bottom: 0.5rem;
          font-family: "Montserrat", sans-serif;
        }
        @media (min-width: 768px) {
          .cta-content h2 {
            font-size: 1.75rem;
            margin-bottom: 0.75rem;
          }
        }

        .cta-content p {
          opacity: 0.9;
          max-width: 500px;
          margin: 0 auto 1.25rem;
          font-size: 0.875rem;
        }
        @media (min-width: 768px) {
          .cta-content p {
            font-size: 1rem;
            margin-bottom: 1.5rem;
          }
        }

        .cta-buttons {
          display: flex;
          gap: 0.75rem;
          justify-content: center;
          flex-wrap: wrap;
        }
        @media (min-width: 768px) {
          .cta-buttons {
            gap: 1rem;
          }
        }

        /* Responsive Overrides */
        @media (max-width: 576px) {
          .contact-form-wrapper {
            padding: 0;
          }

          .contact-info-item {
            padding: 0.75rem;
          }

          .faq-card {
            padding: 0.875rem 1rem;
          }

          .page-hero {
            padding: 2rem 1rem;
          }

          .contact-section {
            padding: 1.5rem 0;
          }

          .faq-section {
            padding: 1.5rem 0;
          }

          .cta-section {
            padding: 1.5rem 1rem;
          }

          .success-message {
            padding: 1.25rem;
          }
        }

        @media (min-width: 576px) and (max-width: 768px) {
          .contact-grid {
            gap: 1.5rem;
          }

          .contact-info-list {
            gap: 0.75rem;
          }
        }

        /* Touch-friendly tap targets */
        .contact-form .form-group input,
        .contact-form .form-group select,
        .contact-form .form-group textarea,
        .contact-info-item,
        .faq-card,
        .quick-contact-btn {
          min-height: 44px;
        }

        .contact-form .form-group textarea {
          min-height: 100px;
        }
      `}</style>
    </motion.div>
  );
};

export default Contact;
